/**
 * Manually derived from: https://shopify.dev/api/admin/rest/reference/orders/refund
 * API Version: 2022-01
 */

import { FieldType } from "../../type-system/FieldType";
import {
  ShopifyLocationKey,
  ShopifyOrderLineItemKey,
  ShopifyRefundKey,
  ShopifyRefundLineItemKey,
  ShopifyRefundLineItemToLocationKey,
  ShopifyRefundLineItemToOrderLineItemKey,
  ShopifyRefundLineItemToRefundKey,
  ShopifyRefundLineItemToShopKey,
  ShopifyShopKey,
} from "./keys";
import { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";

const specs: ConnectionModelSpecVersion = {
  key: ShopifyRefundLineItemKey,
  "2022-01": {
    name: "Shopify Refund Line Item",
    fields: [
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "id" } },
      },
      {
        name: "Refund",
        fieldType: FieldType.BelongsTo,
        key: ShopifyRefundLineItemToRefundKey,
        configuration: {
          relatedModelKey: ShopifyRefundKey,
        },
      },
      {
        name: "Order Line Item",
        fieldType: FieldType.BelongsTo,
        key: ShopifyRefundLineItemToOrderLineItemKey,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "line_item_id" } },
        configuration: {
          relatedModelKey: ShopifyOrderLineItemKey,
        },
      },
      {
        name: "Quantity",
        fieldType: FieldType.Number,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "quantity" } },
      },
      {
        name: "Restock Type",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "restock_type" } },
      },
      {
        name: "Location",
        fieldType: FieldType.BelongsTo,
        key: ShopifyRefundLineItemToLocationKey,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "location_id" } },
        configuration: {
          relatedModelKey: ShopifyLocationKey,
        },
      },
      {
        name: "Subtotal",
        fieldType: FieldType.Number,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "subtotal" } },
      },
      {
        name: "Total Tax",
        fieldType: FieldType.Number,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "total_tax" } },
      },
      {
        name: "Subtotal Set",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "subtotal_set" } },
      },
      {
        name: "Total Tax Set",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "total_tax_set" } },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifyRefundLineItemToShopKey,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "shop_id" } },
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
    ],
    syncConfig: { type: "rest_list", resource: "order" },
  },
  "2025-04": {
    fields: [
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "id", graphQLFieldKey: "id", availableInWebhook: ["refunds/create"] },
        },
      },
      {
        name: "Refund",
        fieldType: FieldType.BelongsTo,
        key: ShopifyRefundLineItemToRefundKey,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "refundId", skipGraphQLSelection: true } },
        configuration: {
          relatedModelKey: ShopifyRefundKey,
        },
      },
      {
        name: "Order Line Item",
        fieldType: FieldType.BelongsTo,
        key: ShopifyRefundLineItemToOrderLineItemKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "line_item_id",
            graphQLFieldKey: "lineItem",
            graphQLSelection: "id",
            availableInWebhook: ["refunds/create"],
          },
        },
        configuration: {
          relatedModelKey: ShopifyOrderLineItemKey,
        },
      },
      {
        name: "Quantity",
        fieldType: FieldType.Number,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "quantity", graphQLFieldKey: "quantity", availableInWebhook: ["refunds/create"] },
        },
      },
      {
        name: "Restock Type",
        fieldType: FieldType.Enum,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "restock_type",
            graphQLFieldKey: "restockType",
            isShopifyEnum: true,
            availableInWebhook: ["refunds/create"],
          },
        },
        configuration: {
          allowOther: true,
          allowMultiple: false,
          options: [{ name: "CANCEL" }, { name: "LEGACY_RESTOCK" }, { name: "NO_RESTOCK" }, { name: "RETURN" }],
        },
      },
      {
        name: "Location",
        fieldType: FieldType.BelongsTo,
        key: ShopifyRefundLineItemToLocationKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "location_id",
            graphQLFieldKey: "location",
            graphQLSelection: "id",
            availableInWebhook: ["refunds/create"],
          },
        },
        configuration: {
          relatedModelKey: ShopifyLocationKey,
        },
      },
      {
        name: "Subtotal",
        fieldType: FieldType.Number,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "subtotal",
            graphQLFieldKey: "subtotal",
            skipGraphQLSelection: true,
            availableInWebhook: ["refunds/create"],
          },
        },
      },
      {
        name: "Total Tax",
        fieldType: FieldType.Number,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "total_tax",
            graphQLFieldKey: "totalTax",
            skipGraphQLSelection: true,
            availableInWebhook: ["refunds/create"],
          },
        },
      },
      {
        name: "Subtotal Set",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            availableInWebhook: ["refunds/create"],
            shopifyFieldKey: "subtotal_set",
            graphQLFieldKey: "subtotalSet",
            graphQLSelection: `
          shop_money:shopMoney {
            amount
            currency_code:currencyCode
          }
          presentment_money:presentmentMoney {
            amount
            currency_code:currencyCode
          }
          `,
          },
        },
      },
      {
        name: "Total Tax Set",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            availableInWebhook: ["refunds/create"],
            shopifyFieldKey: "total_tax_set",
            graphQLFieldKey: "totalTaxSet",
            graphQLSelection: `
          shop_money:shopMoney {
            amount
            currency_code:currencyCode
          }
          presentment_money:presentmentMoney {
            amount
            currency_code:currencyCode
          }
          `,
          },
        },
      },
      {
        name: "Price Set",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "priceSet",
            graphQLOnlyField: true,
            graphQLSelection: `
          shopMoney {
            amount
            currency_code:currencyCode
          }
          presentmentMoney {
            amount
            currency_code:currencyCode
          }
          `,
          },
        },
      },
    ],
    syncConfig: {
      type: "graphql_sublist",
      parent: "order.refunds",
      resource: "refund.refundLineItems",
      additionalFields: ["refundId:id"],
    },
  },
};

export const ShopifyRefundLineItem = ({
  apiVersion,
  frameworkVersionConstraint,
  webhookTopicsForModel,
}: {
  apiVersion: ShopifyApiVersion;
  frameworkVersionConstraint?: string;
  webhookTopicsForModel?: string[];
}) => buildConnectionSpec(specs, apiVersion, frameworkVersionConstraint, webhookTopicsForModel);
